import React from 'react';
import styled, { css } from 'styled-components';

import type { IconType } from '@zimpel/common/components/Icon';
import type HTMLProps from '@zimpel/common/HTMLProps';

const SwitchButtonBar = styled.div<{ $withMargin?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  grid-gap: 5px 2px;
  ${({ $withMargin }) => $withMargin && css`
    margin-bottom: 10px;
  `}
`;

type StyledSwitchButtonProps = {
  $active?: boolean;
}

const StyledSwitchButton = styled.button<StyledSwitchButtonProps>`
  --height: 20px;
  --min-width: 16px;

  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  min-width: var(--min-width);
  height: var(--height);
  padding: 2px 4px;
  border: 1px solid var(--color-primary-element);
  border-radius: 2px;
  color: ${({ $active }) => ($active ? 'var(--color-text-bright)' : 'var(--color-primary-element)')};
  background-color:
    ${({ $active }) => (
    $active ? 'var(--color-primary-element)' : 'transparent'
  )};
  ${({ disabled }) => disabled && css`
    opacity: 0.5;
  `}

  & > * {
    min-width: var(--min-width);
    height: 100%;
    text-align: center;
  }

  &:hover:not([disabled]) {
    background-color: var(--color-primary-element);
    color: var(--color-text-bright);
  }
`;

type SwitchButtonProps = {
  active?: boolean;
  icon?: IconType;
  disabled?: boolean;
  type: 'button' | 'submit';
} & HTMLProps & React.PropsWithChildren;

const SwitchButton: React.FC<SwitchButtonProps> = ({
  active, icon, children, ...props
}) => (
  <StyledSwitchButton $active={active} {...props}>
    {icon && React.createElement(icon, { fixedWidth: true, rightMargin: false, size: 'xs' })}
    {children}
  </StyledSwitchButton>
);

const widthToMinWidth = {
  xs: '26px',
  s: '80px',
  m: '100px',
  l: '130px',
  xl: '265px',
  fullWidth: '100%',
};

const heightToHeight = {
  s: '16px',
  m: 'var(--input-height-big)',
  l: '40px',
};

const heightToFontSize = {
  s: 'var(--font-size-small)',
  m: 'var(--font-size-default)',
  l: 'var(--font-size-large)',
};

const heightToHorizontalPadding = {
  s: '8px',
  m: '12px',
  l: '16px',
};

export type BaseButtonProps = {
  $width?: 'xs' | 's' | 'm' | 'l' | 'xl' | 'fullWidth';
  $height?: 's' | 'm' | 'l';
  disabled?: boolean; // Don't know why this does not work without this
  type?: 'submit' | 'button'; // Don't know why this does not work without this
} & HTMLProps;

const BaseButton = styled.button.attrs<BaseButtonProps>((props) => ({
  ...props,
  $height: props.$height || 'm',
}))`
  --border-width: 1px;

  box-sizing: border-box;
  display: inline-block;
  text-decoration: none;
  text-align: center;
  cursor: pointer;
  border: var(--border-width) solid var(--color-primary-element);
  border-radius: 4px;
  ${({ $height }) => $height && css`
    height: ${heightToHeight[$height]};
    font-size: ${heightToFontSize[$height]};
    padding-left: ${heightToHorizontalPadding[$height]};
    padding-right: ${heightToHorizontalPadding[$height]};

    /* We need to set the line-height otherwise a button as link would not be correctly aligned */
    line-height: calc(${heightToHeight[$height]} - (2 * var(--border-width)));
  `}

  ${({ $width }) => $width && css`
    min-width: ${widthToMinWidth[$width]};
  `}

  &[disabled] {
    cursor: default;
    opacity: 0.5;
  }

  &:hover:not([disabled]) {
    color: var(--color-text-bright);
    background-color: var(--color-primary-element-hover);
    border-color: var(--color-primary-element-hover);
  }
`;

const PrimaryButton = styled(BaseButton)`
  color: var(--color-text-bright);
  background-color: var(--color-primary-element);
`;

const SecondaryButton = styled(BaseButton)`
  color: var(--color-primary-element);
  background-color: transparent;
`;

type BaseLinkButtonProps = {
  type: 'submit' | 'button';
} & HTMLProps;

const BaseLinkButton = styled.button<BaseLinkButtonProps>`
  display: inline;
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    background-color: transparent;
    color: var(--color-primary-element-hover);
  }

  &[disabled],
  &[disabled]:hover {
    text-decoration: none;
    cursor: default;
    opacity: 0.5;
  }
`;

const PrimaryLinkButton = styled(BaseLinkButton)`
  color: var(--color-primary-element);

  &[disabled],
  &[disabled]:hover {
    color: var(--color-primary-element);
  }
`;

const SecondaryLinkButton = styled(BaseLinkButton)`
  color: var(--color-text-default);

  &[disabled],
  &[disabled]:hover {
    color: var(--color-text-default);
  }
`;

export {
  PrimaryButton,
  SecondaryButton,
  SwitchButtonBar,
  SwitchButton,
  PrimaryLinkButton,
  SecondaryLinkButton,
};
